import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const CMSVisibilityContext = createContext();

export function CMSVisibilityProvider({ children }) {
  const [visibility, setVisibility] = useState({ sections: {}, buttons: {} });
  const [loading, setLoading] = useState(true);

  const loadVisibility = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/public/cms-visibility`);
      setVisibility(res.data || { sections: {}, buttons: {} });
    } catch (error) {
      console.error('Failed to load CMS visibility');
      setVisibility({ sections: {}, buttons: {} });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVisibility();
  }, [loadVisibility]);

  // Check if a section/button is visible (default to true if not set)
  const isVisible = useCallback((category, key) => {
    if (category === 'buttons') {
      return visibility.buttons?.[key] !== false;
    }
    return visibility.sections?.[key] !== false;
  }, [visibility]);

  // Convenience functions for specific categories
  const isLandingVisible = useCallback((key) => isVisible('sections', key), [isVisible]);
  const isDashboardVisible = useCallback((key) => isVisible('sections', key), [isVisible]);
  const isGlobalVisible = useCallback((key) => isVisible('sections', key), [isVisible]);
  const isButtonVisible = useCallback((key) => isVisible('buttons', key), [isVisible]);

  const value = {
    visibility,
    loading,
    isVisible,
    isLandingVisible,
    isDashboardVisible,
    isGlobalVisible,
    isButtonVisible,
    refreshVisibility: loadVisibility
  };

  return (
    <CMSVisibilityContext.Provider value={value}>
      {children}
    </CMSVisibilityContext.Provider>
  );
}

export function useCMSVisibility() {
  const context = useContext(CMSVisibilityContext);
  if (!context) {
    throw new Error('useCMSVisibility must be used within a CMSVisibilityProvider');
  }
  return context;
}

export default CMSVisibilityContext;
